import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, FlaskConical } from 'lucide-react';
import { WorldSection } from './WorldPageHeader';
import { MaterialList } from '../progression/MaterialList';
import { EntityThumb } from './EntityThumb';
import { NpcCard } from '../progression/NpcCard';
import { RecipeSteps, ClassicRecipeLabel } from '../progression/RecipeSteps';
import { ChecklistBar } from '../progression/ChecklistBar';
import { useChecklist } from '../../hooks/useChecklist';
import { useWorldLookup } from '../../hooks/useWorldLookup';
import { recipePath } from '../../data/guide/recipes';
import { recipesProducing, recipesUsing } from '../../utils/recipeLinks';
import type { Recipe } from '../../types/progression';

/** "Cómo crearlo": recetas cuyo resultado es este ítem, con materiales, NPC y pasos. */
export function HowToCraft({
  id,
  name
}: {
  id: string;
  name: string;
}) {
  const list = recipesProducing(id, name);
  if (!list.length) return null;
  return <WorldSection id="como-crearlo" title="Cómo crearlo" count={list.length > 1 ? list.length : undefined} data-id="element-1391" data-mp="3vaz90nv1rmpu">
      <div className="space-y-6" data-id="element-1392" data-mp="36wuvjgeld1w">
        {list.map(r => <CraftCard key={r.id} r={r} data-id="element-1393" data-mp="36dlir4tjwzh" />)}
      </div>
    </WorldSection>;
}
function CraftCard({
  r
}: {
  r: Recipe;
}) {
  const {
    status,
    lookup
  } = useWorldLookup();
  const list = useChecklist(`receta-${r.id}`);
  const groups = r.variants ? r.variants.map(v => ({
    prefix: v.id,
    title: v.title,
    ingredients: v.ingredients
  })) : [{
    prefix: 'ing',
    title: '',
    ingredients: r.ingredients
  }];
  const keys = groups.flatMap(g => g.ingredients.map(m => `${g.prefix}:${m.id}`));
  const done = keys.filter(k => list.isChecked(k)).length;
  return <div className="space-y-4" data-id="element-1394" data-mp="37l4xc9mgs476">
      <div className="flex flex-wrap items-center gap-2" data-id="element-1395" data-mp="37m6bimys16t4">
        <h3 className="font-cinzel text-base font-bold text-white md:text-lg" data-id="element-1396" data-mp="2sodz43oj0yj5">{r.title}</h3>
        <ClassicRecipeLabel data-id="element-1397" data-mp="2xn7bfss2ujhq" />
      </div>
      <dl className="grid gap-2 text-sm sm:grid-cols-2" data-id="element-1398" data-mp="2xm5pgt9iwcl8">
        <div className="rounded-xl bg-white/[0.03] px-3 py-2" data-id="element-1399" data-mp="2eilray4pak3z">
          <dt className="text-xs text-gray-400" data-id="element-1400" data-mp="2ejwuavqbnnu1">Dónde se combina</dt>
          <dd className="font-semibold text-white" data-id="element-1401" data-mp="29quahfbcjsr1">{r.machine ?? 'Chaos Machine'}</dd>
        </div>
        <div className="rounded-xl bg-white/[0.03] px-3 py-2" data-id="element-1402" data-mp="24tu3912rgjyc">
          <dt className="text-xs text-gray-400" data-id="element-1403" data-mp="1ztpnjd9oft96">Zen</dt>
          <dd className="font-semibold text-white" data-id="element-1404" data-mp="1zv91uybrvu8b">{r.zen}</dd>
        </div>
      </dl>
      <NpcCard npcId={r.npc} lookup={lookup} data-id="element-1405" data-mp="2xg52o8emsetk" />
      <ChecklistBar list={list} total={keys.length} done={done} data-id="element-1406" data-mp="2xf3npsuhv86k" />
      {groups.map(g => <div key={g.prefix} data-id="element-1407" data-mp="2sgir03egb6mv">
          {g.title && <p className="mb-2 text-sm font-semibold text-[#D4AF37]" data-id="element-1408" data-mp="2nih6rf5ah19t">{g.title}</p>}
          <MaterialList materials={g.ingredients} list={list} prefix={g.prefix} lookup={lookup} status={status} fromRecipe={r.id} data-id="element-1409" data-mp="2ikflcnke4pv6" />
        </div>)}
      {r.steps && r.steps.length > 0 && <div data-id="element-1410" data-mp="175a6ymawd2vq">
          <p className="mb-2 text-sm font-semibold text-gray-200" data-id="element-1411" data-mp="174r0c1qp60x0">Pasos</p>
          <RecipeSteps steps={r.steps} data-id="element-1412" data-mp="3isdo1tnpnplf" />
        </div>}
      <p className="text-xs text-gray-500" data-id="element-1413" data-mp="3iu6yzl5cwvz8">
        Probabilidad de éxito y efectos del fallo: pendientes de confirmar en Vanguard. Revisa lo que muestra la ventana antes de combinar.
        {r.source && ` Fuente de referencia: ${r.source}.`}
      </p>
      <Link to={recipePath(r.id)} className="inline-flex h-10 items-center gap-2 rounded-full border border-[#D4AF37]/40 px-4 text-sm font-semibold text-[#F3E5AB] transition-colors duration-150 hover:bg-[#D4AF37]/10" data-id="element-1414" data-mp="1ce2mhbqymgt">
        Abrir guía completa
        <ArrowRight className="h-4 w-4" aria-hidden="true" data-id="element-1415" data-mp="aar2u52bu3z0" />
      </Link>
    </div>;
}

/** "Se utiliza para crear…": recetas donde este ítem o variante exacta es ingrediente. */
export function UsedToCraft({
  id,
  name
}: {
  id: string;
  name: string;
}) {
  const list = recipesUsing(id, name);
  if (!list.length) return null;
  return <WorldSection id="se-utiliza-para" title="Se utiliza para crear…" count={list.length} data-id="element-1416" data-mp="2m1v0aq9aogsi">
      <ul className="grid gap-2 sm:grid-cols-2" data-id="element-1417" data-mp="gduc5e86dxoi">
        {list.map(({
        recipe: r,
        material: m
      }) => <li key={r.id} data-id="element-1418" data-mp="beq0mgxs3nv0">
            <Link to={recipePath(r.id)} className="group flex h-full items-center gap-3 rounded-2xl border border-white/10 bg-[#0A1628] p-3.5 transition-colors duration-150 hover:border-[#D4AF37]/40" data-id="element-1419" data-mp="bfj150659h4p">
              <EntityThumb entity={(() => {
            const rid = (r.results.find(x => x.itemId) ?? r.variants?.flatMap(v => v.results).find(x => x.itemId))?.itemId;
            return rid ? {
              type: 'item' as const,
              id: rid
            } : undefined;
          })()} name={r.shortTitle} fallback={<FlaskConical className="h-5 w-5 shrink-0 text-[#D4AF37]" aria-hidden="true" data-id="element-1421" data-mp="6hrae9h8k123" />} data-id="element-1420" data-mp="lcnnazzzpcli" />
              <span className="min-w-0 flex-1" data-id="element-1422" data-mp="1n3ie0n0woir">
                <span className="block font-semibold text-white" data-id="element-1423" data-mp="3a3omp44pmtfj">{r.shortTitle}</span>
                <span className="block text-xs text-gray-400" data-id="element-1424" data-mp="363xz19ahctf8">{m.qty ? `${m.qty} en la receta · ` : ''}referencia clásica</span>
              </span>
              <ArrowRight className="h-4 w-4 shrink-0 text-gray-500 group-hover:text-[#D4AF37]" aria-hidden="true" data-id="element-1425" data-mp="2vx4t1fh39vna" />
            </Link>
          </li>)}
      </ul>
    </WorldSection>;
}